import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * Polls /api/live/snapshot every 30s and exposes a `{fixtureId → live row}`
 * lookup to any consumer.  One network call → all prediction cards on the
 * page get a synchronized LIVE overlay (badge + minute + score).
 */

const LiveSnapshotContext = createContext({
  byId: {},
  liveCount: 0,
  fetchedAt: null,
  isLive: () => false,
  getLive: () => null,
  refresh: () => {},
});

export const LiveSnapshotProvider = ({ children, pollMs = 30000 }) => {
  const [snapshot, setSnapshot] = useState({ byId: {}, liveCount: 0, fetchedAt: null });

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/live/snapshot`);
      setSnapshot({
        byId: data?.fixtures || {},
        liveCount: data?.live_count || 0,
        fetchedAt: data?.fetched_at,
      });
    } catch {
      // Silent — live overlays simply won't render until next tick
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs]);

  const isLive = useCallback(
    (fid) => !!snapshot.byId[String(fid)],
    [snapshot]
  );
  const getLive = useCallback(
    (fid) => snapshot.byId[String(fid)] || null,
    [snapshot]
  );

  return (
    <LiveSnapshotContext.Provider value={{ ...snapshot, isLive, getLive, refresh }}>
      {children}
    </LiveSnapshotContext.Provider>
  );
};

export const useLiveSnapshot = () => useContext(LiveSnapshotContext);
